/**
 * Pure decisions for the soil sign-off queue (U3).
 *
 * Thresholds are constants, NOT tenant configuration: a tenant setting needs a
 * settings surface, a migration and a backend read, none of which this slice
 * has. They live here so a later slice can lift them to config without
 * touching the page (spec §10 open item 2).
 *
 * THE AGEING RULES NOW LIVE IN ./signOffAgeing (F3), shared by every sign-off
 * surface in Nutrition — not just this file's two soil consumers. This file
 * re-exports them so the session detail screen's module (./soilSessionDetail),
 * which imports `waitingDaysSince`, `waitingTone` and `WAITING_DANGER_DAYS`
 * from here, and SoilSignOffPage need no changes at all.
 */
import { SoilAwaitingGroup } from '../../services/soilGroupService';
// The ageing rules moved to ./signOffAgeing in F3 so the foliar sign-off queue
// and foliar session detail share ONE definition with soil's two surfaces
// rather than growing a third copy. Imported (for this file's own use below)
// and re-exported here so every existing consumer (SoilSignOffPage,
// soilSessionDetail) is unchanged.
import {
  WAITING_WARN_DAYS, WAITING_DANGER_DAYS, waitingTone, waitingDaysSince, WaitingTone,
} from './signOffAgeing';

export {
  WAITING_WARN_DAYS, WAITING_DANGER_DAYS, waitingTone, waitingDaysSince,
};
// `export type`, NOT a bare `export` — CRA compiles with isolatedModules:true,
// under which re-exporting a TYPE through a value export is a BUILD failure
// that jest cannot see.
export type { WaitingTone };

export interface SignOffRow extends SoilAwaitingGroup {
  waitingDays: number;
  tone: WaitingTone;
}

export function buildSignOffRows(items: SoilAwaitingGroup[], now: Date): SignOffRow[] {
  return (items || []).map((it) => {
    const days = waitingDaysSince(it.completedAt, now);
    return { ...it, waitingDays: days, tone: waitingTone(days) };
  });
}

export function filterSignOffRows(rows: SignOffRow[], search: string, overThresholdOnly: boolean): SignOffRow[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (overThresholdOnly && r.waitingDays < WAITING_WARN_DAYS) return false;
    if (!q) return true;
    const hay = [r.productName, r.label, r.month].concat(r.orchardNames || []).join(' ').toLowerCase();
    return hay.indexOf(q) !== -1;
  });
}

export function toBulkItems(rows: SignOffRow[], selectedIds: string[]): Array<{ sessionId: string; groupId: string }> {
  const byId: Record<string, SignOffRow> = {};
  rows.forEach((r) => { byId[r.id] = r; });
  return selectedIds
    .map((id) => byId[id])
    .filter(Boolean)
    .map((r) => ({ sessionId: r.sessionId, groupId: r.groupId }));
}

/** Labels for the items a bulk run did not sign off, so a partial result is named. */
export function failedLabels(
  rows: SignOffRow[],
  results: Array<{ sessionId: string; groupId: string; ok: boolean }>,
): string[] {
  return results
    .filter((r) => !r.ok)
    .map((r) => rows.find((row) => row.sessionId === r.sessionId && row.groupId === r.groupId))
    .filter(Boolean)
    .map((row) => (row as SignOffRow).label);
}
